import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import {
    ArrayFormField,
    ButtonFormField,
    CheckboxFormField,
    FormField,
    LabelFormField,
    NumberFormField,
    SingleSelectionFormField,
    TextFormField,
    ToggleFormField,
} from "src/app/shared/form/formField.model";
import { DisplayFormats } from "src/app/shared/form/displayFormat.constants";
import { TurnAroundTimeDialogComponent } from "../turn-around-time-dialog/turn-around-time-dialog.component";
import { LabService } from "src/app/services/lab.service";
import { Lab, TAT } from "src/app/models/lab.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { OrgLevelTypeEnum } from "src/app/enums/orgLevelType.enum";
import { AllowedIntegrationType } from "src/app/enums/IntegrationType.enum";
import { Lookup } from "src/app/models/lookup.model";
import { getEnumLookup } from "src/app/_helpers/enum-helper";

@Component({
  standalone: false,
    selector: "app-lab-integration-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/form/form.component.scss",
        "./lab-integration-dialog.component.scss",
    ],
})
export class LabIntegrationDialogComponent
    extends FormComponent<Lab>
    implements OnInit
{
    tatFieldsList: FormField[][] = [];
    organizationLevels: Lookup[];

    constructor(
        dialogRef: MatDialogRef<LabIntegrationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        labService: LabService,
        protected dialog: MatDialog,
        protected injector: Injector,
        private organizationLevelService: OrganizationLevelService,
    ) {
        super(dialogRef, labService, data, "Add Lab", injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            orgLevels: this.organizationLevelService.getAll(
                JSON.stringify({ orgLevelType: OrgLevelTypeEnum.Site }),
            ),
        }).subscribe(
            (result) => {
                this.entity = result.entity;
                this.organizationLevels = result.orgLevels;
                this.initFields();
                this.isFormLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        );
    }

    getNewObject(): Lab {
        return new Lab();
    }

    validate(): boolean {
        return true;
    }

    initFields(): void {
        const emptyTertiaryField = new LabelFormField({
            isVisible: () => true,
            property: "",
            name: "",
            columnSpan: new ColumnSpan(1, 3),
            required: false,
            cssClass: "empty-field",
        });

        const lineField = new LabelFormField({
            isVisible: () => true,
            property: "",
            name: "",
            columnSpan: new ColumnSpan(1, 1),
            cssClass: "form-hr",
            required: false,
        });

        const sectionHeader = (title: string) =>
            new LabelFormField({
                isVisible: () => true,
                property: "",
                name: title,
                columnSpan: new ColumnSpan(1, 3),
                cssClass: "pt-3 text-uppercase text-bold",
                required: false,
            });

        const labDetailFields: FormField[] = [
            sectionHeader("Lab Details"),
            new SingleSelectionFormField({
                property: nameOf((x: Lab) => x.labIntegrationType),
                name: "Integration Type",
                columnSpan: new ColumnSpan(1, 3),
                options: getEnumLookup(AllowedIntegrationType),
                required: true,
            }),
            new SingleSelectionFormField({
                property: nameOf((x: Lab) => x.organizationLevelId),
                name: "Organization Level",
                columnSpan: new ColumnSpan(1, 3),
                options: this.organizationLevels,
                required: true,
            }),
            new ToggleFormField({
                property: nameOf<Lab>(x => x.applyBlankCorrection),
                name: "Apply Blank Correction",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            emptyTertiaryField,
            lineField,
        ];

        const reportFields: FormField[] = [
            sectionHeader("Report To"),
            new TextFormField({
                property: nameOf((x: Lab) => x.reportTo),
                name: "Report To",
                columnSpan: new ColumnSpan(1, 3),
                required: true,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToCompanyName),
                name: "Company Name",
                columnSpan: new ColumnSpan(1, 3),
                required: true,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToAddress1),
                name: "Address 1",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToAddress2),
                name: "Address 2",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToCity),
                name: "City",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToState),
                name: "State",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToZip),
                name: "Zip",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToPhoneNumber),
                name: "Phone Number",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToCellNumber),
                name: "Cell Number",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.reportToEmail),
                name: "Email",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            lineField,
        ];

        const invoiceAddressFields: TextFormField[] = [
            new TextFormField({
                property: nameOf((x: Lab) => x.invoiceTo),
                name: "Invoice To",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.invoiceCompanyName),
                name: "Company Name",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.invoiceAddress1),
                name: "Address 1",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.invoiceAddress2),
                name: "Address 2",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.invoicePhoneNumber),
                name: "Phone No.",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
        ];

        const syncInvoiceAddress = (() => {
            let snapshot: { invoiceTo: string; companyName: string; addr1: string; addr2: string; phone: string } | null = null;
            return (checked: boolean) => {
                if (checked) {
                    snapshot = {
                        invoiceTo: this.entity.invoiceTo ?? '',
                        companyName: this.entity.invoiceCompanyName ?? '',
                        addr1: this.entity.invoiceAddress1 ?? '',
                        addr2: this.entity.invoiceAddress2 ?? '',
                        phone: this.entity.invoicePhoneNumber ?? '',
                    };
                    this.entity.invoiceTo = this.entity.reportTo ?? '';
                    this.entity.invoiceCompanyName = this.entity.reportToCompanyName ?? '';
                    this.entity.invoiceAddress1 = this.entity.reportToAddress1 ?? '';
                    this.entity.invoiceAddress2 = this.entity.reportToAddress2 ?? '';
                    this.entity.invoicePhoneNumber = this.entity.reportToPhoneNumber ?? '';
                } else if (snapshot) {
                    this.entity.invoiceTo = snapshot.invoiceTo;
                    this.entity.invoiceCompanyName = snapshot.companyName;
                    this.entity.invoiceAddress1 = snapshot.addr1;
                    this.entity.invoiceAddress2 = snapshot.addr2;
                    this.entity.invoicePhoneNumber = snapshot.phone;
                    snapshot = null;
                }
                invoiceAddressFields.forEach(f => { if (f instanceof TextFormField) { f.readonly = checked; } });
            };
        })();

        const invoiceFields: FormField[] = [
            sectionHeader("Invoice To"),
            new TextFormField({
                property: nameOf((x: Lab) => x.invoiceComments),
                name: "Comments",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new CheckboxFormField({
                property: nameOf((x: Lab) => x.sameAsReportToAddress),
                name: "Same as Report To Address",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
                onChange: (event: { checked: boolean }) => syncInvoiceAddress(event.checked),
            }),
            ...invoiceAddressFields,
            new TextFormField({
                property: nameOf((x: Lab) => x.invoicePoNumber),
                name: "PO No.",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.invoiceEmail),
                name: "Email",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.invoicePaymentInfo),
                name: "Payment Info",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            lineField,
        ];

        const labAddressFields: FormField[] = [
            sectionHeader("Lab Address"),
            new TextFormField({
                property: nameOf((x: Lab) => x.labName),
                name: "Lab Name",
                columnSpan: new ColumnSpan(1, 3),
                required: true,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.labAccountNumber),
                name: "Lab Account Number",
                columnSpan: new ColumnSpan(1, 3),
                isVisible: () =>
                    this.entity?.labIntegrationType !== AllowedIntegrationType.AlsLab &&
                    this.entity?.labIntegrationType !== AllowedIntegrationType.OtherLab,
                required: true,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.labAccountNumber),
                name: "ALS Quote Number",
                columnSpan: new ColumnSpan(1, 3),
                isVisible: () => this.entity?.labIntegrationType === AllowedIntegrationType.AlsLab,
                required: true,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.labAccountNumber),
                name: "Lab Account Number",
                columnSpan: new ColumnSpan(1, 3),
                isVisible: () => this.entity?.labIntegrationType === AllowedIntegrationType.OtherLab,
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.labAddress1),
                name: "Address 1",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.labAddress2),
                name: "Address 2",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.labCity),
                name: "City",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.labState),
                name: "State",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.labZip),
                name: "Zip",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.labPhoneNumber),
                name: "Phone Number",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            emptyTertiaryField,
            new TextFormField({
                property: nameOf((x: Lab) => x.labCellNumber),
                name: "Cell Number",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
            new TextFormField({
                property: nameOf((x: Lab) => x.labEmail),
                name: "Email",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
            }),
        ];

        const tatfields = [
            lineField,
            sectionHeader("TAT"),
            emptyTertiaryField,
            new ButtonFormField({
                property: "",
                name: "Add TAT",
                columnSpan: new ColumnSpan(1, 3),
                cssClass: "add-tat",
                onClick: () => this.openTatDialog(),
                required: false,
            }),
            new ArrayFormField({
                property: "tats",
                name: "",
                isVisible: () => this.tatFieldsList.length > 1,
                items: this.tatFieldsList,
                cssClass: "multiple-rows tat-list",
                columnSpan: new ColumnSpan(1, 1),
                required: false,
            }),
        ];

        const minBlankCountField = new NumberFormField({
            property: nameOf<Lab>(x => x.minBlankCount),
            name: "Minimum Blank Count",
            columnSpan: new ColumnSpan(1, 3),
            required: false,
            readonly: !this.entity?.enforceBlankRules,
            min: 0,
            displayFormat: DisplayFormats.Integer,
        });
        const minBlankRatioField = new NumberFormField({
            property: nameOf<Lab>(x => x.minBlankRatioPercent),
            name: "Minimum Blank Ratio (%)",
            columnSpan: new ColumnSpan(1, 3),
            required: false,
            readonly: !this.entity?.enforceBlankRules,
            min: 0,
            max: 100,
            displayFormat: DisplayFormats.Integer,
        });
        const blankQcRulesFields: FormField[] = [
            sectionHeader("Blank QC Rules"),
            new ToggleFormField({
                property: nameOf<Lab>(x => x.enforceBlankRules),
                name: "Enforce Blank Rules",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
                onChange: (event: { checked: boolean }) => {
                    minBlankCountField.readonly = !event.checked;
                    minBlankRatioField.readonly = !event.checked;
                },
            }),
            emptyTertiaryField,
            emptyTertiaryField,
            minBlankCountField,
            minBlankRatioField,
            emptyTertiaryField,
            lineField,
        ];

        this.fields = [
            ...labDetailFields,
            ...blankQcRulesFields,
            ...reportFields,
            ...invoiceFields,
            ...labAddressFields,
            ...tatfields,
        ];

        if (this.entity.tats) {
            this.addTats(this.entity.tats);
        }

        if (this.entity.sameAsReportToAddress) {
            syncInvoiceAddress(true);
        }
    }

    openTatDialog(entityId?: string) {
        const dialogRef = this.dialog.open(TurnAroundTimeDialogComponent, {
            data: {
                id: entityId ?? '',
            },
            width: "760px",
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((data: TAT[]) => {
            this.addTats(data);
        });
    }

    private getTatKey(fields: FormField[]): string {
        const tat =
            fields.find((f) => f.property === "turnaroundTime")?.name ?? "";
        const surcharge =
            fields.find((f) => f.property === "surcharge")?.name ?? "";
        return `${tat}__${surcharge}`;
    }

    addTats(data: TAT[] | null) {
        if (data) {
            const tatsHeader = this.createTatFields(
                [{ id: "", surcharge: "", turnAroundTime: "" }],
                true,
            );
            const newTatFields = this.createTatFields(data);
            if (!this.tatFieldsList.length) {
                this.tatFieldsList = [
                    ...tatsHeader,
                    ...this.createTatFields(data),
                ];
            } else {
                const existingKeys = new Set(
                    this.tatFieldsList.map((fields) => this.getTatKey(fields)),
                );

                const uniqueNewFields = newTatFields.filter((fields) => {
                    const key = this.getTatKey(fields);
                    if (existingKeys.has(key)) {
                        return false;
                    }
                    existingKeys.add(key);
                    return true;
                });

                this.tatFieldsList = [
                    ...this.tatFieldsList,
                    ...uniqueNewFields,
                ];
            }

            const tatField = this.fields?.find((f) => f?.property === "tats");
            if (!tatField) return;

            tatField.items = this.tatFieldsList;

            if (!this.entity.tats) {
                this.entity.tats = [];
            }

            data.forEach((item) => {
                const exists = this.entity.tats.some(
                    (t) =>
                        t.turnAroundTime === item.turnAroundTime &&
                        t.surcharge === item.surcharge,
                );

                if (!exists) {
                    this.entity.tats.push(item);
                }
            });
        }
    }

    createTatFields(items: TAT[], isHeader = false): any {
        const fields: FormField[][] = items.map((item) => [
            new LabelFormField({
                property: "",
                name: "",
                columnSpan: new ColumnSpan(1, 3),
                required: false,
                cssClass: "empty-field",
                readonly: true,
            }),
            new LabelFormField({
                property: "turnaroundTime",
                name: isHeader
                    ? "Turn Around Time"
                    : (item.turnAroundTime as string),
                columnSpan: new ColumnSpan(1, 3),
                cssClass: isHeader ? "header-field" : "body-field",
                required: false,
                readonly: true,
            }),
            new LabelFormField({
                property: "surcharge",
                name: isHeader ? "Surcharge" : item.surcharge.toString(),
                columnSpan: new ColumnSpan(1, 3),
                cssClass: isHeader ? "header-field" : "body-field",
                required: false,
                readonly: true,
            }),
        ]);
        return fields;
    }
}
